import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
	/** Form field holding the persisted server image URL (e.g. "img1"). Cleared on remove. */
	urlField: string;
	/** Form field holding the staged File, uploaded out-of-band after save (e.g. "img1File"). */
	fileField: string;
	/** Form field holding the delete flag (e.g. "imgdel1"). */
	delField: string;
	/** Optional per-image navigate-URL field (menu items use "img1nu"/"img2nu"). */
	linkField?: string;
	/** Slot heading, e.g. "Image 1". */
	label: string;
	/** Button label when no image is set. */
	uploadLabel: string;
	/** Button label when an image (server or staged) is present. */
	replaceLabel: string;
	/** Placeholder for the optional link input. */
	linkPlaceholder?: string;
	/** Tailwind classes for the preview/placeholder box (defaults to a landscape thumb). */
	previewClassName?: string;
	/** Comma-separated accept list. */
	accept?: string;
}

const DEFAULT_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * Single image slot shared by the Banner / News / Menu-item editors: shows the existing server image
 * (`url{field}`) or a staged-file preview, with replace/remove and an optional per-image link input.
 * The picked File is stored on the form (`fileField`) for out-of-band upload after save; remove sets
 * the delete flag (`delField`) and clears the URL. The actual upload happens at the page level.
 */
export function ImageSlotField({
	urlField,
	fileField,
	delField,
	linkField,
	label,
	uploadLabel,
	replaceLabel,
	linkPlaceholder,
	previewClassName = 'h-24 w-40',
	accept = DEFAULT_ACCEPT
}: Props) {
	const { t } = useTranslation();
	const { watch, setValue, register } = useFormContext();
	const inputRef = useRef<HTMLInputElement>(null);
	const url = watch(urlField) as string | null;
	const file = watch(fileField) as File | null;
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	// Object URL for the staged file (created/revoked on change to avoid leaks).
	useEffect(() => {
		if (!(file instanceof File)) {
			setPreviewUrl(null);
			return;
		}
		const obj = URL.createObjectURL(file);
		setPreviewUrl(obj);
		return () => URL.revokeObjectURL(obj);
	}, [file]);

	const preview = previewUrl ?? url ?? null;

	const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0];
		e.target.value = '';
		if (!picked) return;
		setValue(fileField, picked, { shouldDirty: true });
		setValue(delField, false, { shouldDirty: true });
	};

	const onRemove = () => {
		setValue(fileField, null, { shouldDirty: true });
		setValue(urlField, null, { shouldDirty: true });
		setValue(delField, true, { shouldDirty: true });
	};

	return (
		<div className="rounded-md border border-border-subtle p-4">
			<p className="mb-3 text-xs font-semibold uppercase tracking-[0.04em] text-foreground-subtle">{label}</p>
			<div className="flex items-start gap-4">
				{preview ? (
					<img src={preview} alt={label} className={`${previewClassName} rounded-md border border-border object-cover`} />
				) : (
					<div className={`${previewClassName} flex items-center justify-center rounded-md border-2 border-dashed border-border-strong`}>
						<ImageIcon size={22} className="text-foreground-disabled" />
					</div>
				)}
				<div className="flex flex-1 flex-col gap-2">
					<div className="flex gap-2">
						<Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
							<Upload size={14} /> {preview ? replaceLabel : uploadLabel}
						</Button>
						{preview && (
							<Button type="button" variant="ghost" size="sm" onClick={onRemove}>
								{t('common.remove')}
							</Button>
						)}
					</div>
					{linkField && <Input {...register(linkField)} placeholder={linkPlaceholder} />}
				</div>
				{/* sr-only (not `hidden`): a programmatic .click() on a display:none input is blocked by some browsers. */}
				<input ref={inputRef} type="file" accept={accept} className="sr-only" onChange={onPick} />
			</div>
		</div>
	);
}
